// 05-11 (medir el 11-11, #244/#249; auditoría final vivo.md §3.6) — los eventos de vista de la oficina: abrir un chat y
// DESDE DÓNDE (la lista, su mesa, Ctrl+K, Alt+↓, un aviso, un enlace de fuera), pulsar Alt+↓/↑, abrir Ctrl+K, abrir la
// ficha ⓘ y abrir una carpeta. Hoy no queda rastro de nada de eso (son vistas: el backend no registra consultas).
//
// Privado por diseño: solo el tipo, el origen y el agente (backend: POST /api/oficina/eventos, tabla oficina_evento,
// migración 109). Sin esperar respuesta y sin errores para quien mira: si falla, se pierde ese evento y ya. Con freno:
// el mismo evento repetido en menos de 1 s cuenta una vez (dobles clics, autorrepetición de una tecla mantenida) y como
// mucho 30 cada 10 s (el servidor tiene además su propio tope). El SQL para leerlo: medir-11-11.md.
import { useEffect } from 'react';
import { oficinaApi } from './api';
import type { OrigenEvento, TipoEvento } from './compartido/vocabulario';
import { useOficinaStore } from './store';

const REPETIDO_MS = 1_000;
const VENTANA_MS = 10_000;
const MAXIMO_EN_VENTANA = 30;

const ultimos = new Map<string, number>();
let ventana = { desde: 0, n: 0 };

/** Apunta un evento de vista (sin esperar ni fallar nunca). */
export function medir(tipo: TipoEvento, origen?: OrigenEvento, agentId?: string | null): void {
  const ahora = Date.now();
  const clave = `${tipo}|${origen ?? ''}|${agentId ?? ''}`;
  const antes = ultimos.get(clave);
  if (antes !== undefined && ahora - antes < REPETIDO_MS) return;
  if (ahora - ventana.desde >= VENTANA_MS) ventana = { desde: ahora, n: 0 };
  if (ventana.n >= MAXIMO_EN_VENTANA) return;
  ventana.n += 1;
  ultimos.set(clave, ahora);
  if (ultimos.size > 200) ultimos.clear();
  try {
    void oficinaApi.evento({ tipo, ...(origen ? { origen } : {}), ...(agentId ? { agentId } : {}) }).catch(() => undefined);
  } catch {
    // Medir nunca rompe lo que Pau está haciendo.
  }
}

/** Solo para las pruebas: vacía el freno. */
export function reiniciarMedir(): void {
  ultimos.clear();
  ventana = { desde: 0, n: 0 };
}

// La dirección con la que se CARGÓ la página (no las navegaciones de dentro): si traía ?agente=, Pau entró a ese chat por
// un enlace de fuera (Telegram, una notificación con la app cerrada, o recargando con el chat abierto).
const AGENTE_AL_CARGAR = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('agente') : null;
let entradaContada = false;

/**
 * Lo que se mide desde la página de la oficina: la entrada por un enlace de fuera (una vez por carga) y las teclas Alt+↓/↑
 * y Ctrl/⌘+K, se atiendan o no (así se ve también cuánto se pulsa Alt+↓ fuera de un chat, donde aún no hace nada). Solo
 * escucha: nunca para ni cambia la tecla.
 */
export function useMedirOficina(agenteAbierto: string | null): void {
  useEffect(() => {
    if (entradaContada || !AGENTE_AL_CARGAR || agenteAbierto !== AGENTE_AL_CARGAR) return;
    entradaContada = true;
    medir('abrir_chat', 'telegram', AGENTE_AL_CARGAR);
  }, [agenteAbierto]);
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const chat = useOficinaStore.getState().chatAbierto;
      if (e.altKey && !e.ctrlKey && !e.metaKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) medir('alt_siguiente', undefined, chat);
      else if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') medir('ctrl_k', undefined, chat);
    };
    window.addEventListener('keydown', tecla, { capture: true, passive: true });
    return () => window.removeEventListener('keydown', tecla, { capture: true });
  }, []);
}
